/**
 * Cargo / pallet weight helpers — mirrors backend app/utils/cargo_weight.py.
 *
 * Dimensions are entered/stored in feet (API fields remain length_cm / width_cm /
 * height_cm for compatibility). CBM and volumetric weight convert ft → cm then
 * apply the original abc.xlsx formulas:
 *   CBM = (L_cm * W_cm * H_cm * Qty) / 1_000_000
 *   VolumetricWeight = (L_cm * W_cm * H_cm * Qty) / 4000
 *   ChargeableWeight = max(actual_per_piece, vol_per_piece) * Qty
 */

/** 1 foot = 30.48 cm */
export const CM_PER_FT = 30.48;

/** Shared presets for bus article type + cargo package name (frontend labels only). */
export const DEFAULT_ARTICLE_TYPE_OPTIONS = [
  'BOX',
  'POLY BAG',
  'BRIEFCASE',
  'PACKAGE',
  'ENVELOPE',
  'PARCEL',
  'CARTON',
  'CRATE',
  'DRUM',
  'BAG',
  'PALLET',
  'VEGETABLE',
  'FLOWER',
  'HOUSE HOLD ITEM',
  'FRUIT',
];

export const GST_EXEMPT_PACKAGE_TYPES = [
  'VEGETABLE',
  'FLOWER',
  'HOUSE HOLD ITEM',
  'HOUSEHOLD ITEM',
  'FRUIT',
];

export const isGstExemptPackageType = (name) => {
  const n = String(name || '').trim().toUpperCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return GST_EXEMPT_PACKAGE_TYPES.includes(n);
};

export const parcelsAreGstExempt = (parcels) =>
  (parcels || []).some((p) =>
    isGstExemptPackageType(p.package_name || p.article_type),
  );

export const PER_PARCEL_CHARGE_KEYS = ['loading', 'unloading', 'door_pickup', 'door_delivery'];

export const isFixedPerParcelCharge = (chargeDefaults, key) => {
  if (!PER_PARCEL_CHARGE_KEYS.includes(key)) return false;
  const cfg = chargeDefaults?.[key];
  if (!cfg) return true;
  return cfg.mode !== 'percent';
};

export const scaledPerParcelCharge = (rate, parcelCount) => {
  const qty = Math.max(0, Number(parcelCount) || 0);
  return Math.round(toNum(rate) * qty * 100) / 100;
};

export const rateFromStoredTotal = (total, parcelCount) => {
  const qty = Math.max(1, Number(parcelCount) || 1);
  return Math.round((toNum(total) / qty) * 100) / 100;
};

export const chargeAmountForSubmit = (chargeDefaults, key, rate, parcelCount) => {
  if (!isFixedPerParcelCharge(chargeDefaults, key)) return toNum(rate);
  return scaledPerParcelCharge(rate, parcelCount);
};

export const emptyParcel = (srNo = 1) => ({
  sr_no: srNo,
  package_name: 'PACKAGE',
  article_type: 'PACKAGE',
  article_amount: '',
  actual_weight: '',
  length_cm: '',
  width_cm: '',
  height_cm: '',
  qty: 1,
  cbm: 0,
  volumetric_weight: 0,
  chargeable_weight: '',
  chargeable_manual: false,
});

export const emptyBusArticle = (srNo = 1) => ({
  sr_no: srNo,
  package_name: 'PACKAGE',
  article_type: 'PACKAGE',
  article_amount: '',
  actual_weight: '',
  qty: 1,
  chargeable_weight: '',
  length_cm: '',
  width_cm: '',
  height_cm: '',
  cbm: 0,
  volumetric_weight: 0,
});

export const isCargoParcelRow = (row) =>
  Boolean(row && Object.prototype.hasOwnProperty.call(row, 'chargeable_manual'));

export const defaultParcelsForMode = (isCargo) =>
  isCargo ? [emptyParcel(1)] : [emptyBusArticle(1)];

export const parcelsMatchingMode = (parcels, isCargo) => {
  const alreadyCargo = isCargoParcelRow(parcels?.[0]);
  if (alreadyCargo === Boolean(isCargo)) return parcels?.length ? parcels : defaultParcelsForMode(isCargo);
  return defaultParcelsForMode(isCargo);
};

export const toNum = (value, fallback = 0) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const ftToCm = (ft) => toNum(ft) * CM_PER_FT;

/** CBM from L/W/H in feet (converted to cm for the standard formula). */
export const calcCbm = (lengthFt, widthFt, heightFt, qty = 1) => {
  const l = ftToCm(lengthFt);
  const w = ftToCm(widthFt);
  const h = ftToCm(heightFt);
  const q = Math.max(toNum(qty, 1), 0);
  return Math.round(((l * w * h * q) / 1_000_000) * 1e6) / 1e6;
};

/** Volumetric wt (kg) from L/W/H in feet. Excel: (L_cm * W_cm * H_cm * Qty) / 4000 — rounded. */
export const calcVolumetricWeight = (lengthFt, widthFt, heightFt, qty = 1) => {
  const l = ftToCm(lengthFt);
  const w = ftToCm(widthFt);
  const h = ftToCm(heightFt);
  const q = Math.max(toNum(qty, 1), 0);
  return Math.round(((l * w * h) / 4000) * q);
};

export const calcDefaultChargeable = (actualWeight, volumetricWeight) =>
  Math.round(Math.max(toNum(actualWeight), toNum(volumetricWeight)));

export const recomputeParcel = (parcel) => {
  const qty = Math.max(toNum(parcel.qty, 1), 1);
  const cbm = calcCbm(parcel.length_cm, parcel.width_cm, parcel.height_cm, qty);
  const volumetric_weight = calcVolumetricWeight(
    parcel.length_cm,
    parcel.width_cm,
    parcel.height_cm,
    qty,
  );
  // Actual wt is per piece; chargeable is the line total (× qty).
  const suggested = calcDefaultChargeable(toNum(parcel.actual_weight) * qty, volumetric_weight);
  const chargeable_weight = parcel.chargeable_manual
    ? Math.round(toNum(parcel.chargeable_weight, suggested))
    : suggested;

  return {
    ...parcel,
    qty,
    cbm,
    volumetric_weight,
    chargeable_weight,
  };
};

/**
 * Validate cargo package rows before save.
 * @returns {string|null} error message or null when valid
 */
export const validateCargoParcels = (parcels, startSrNo = 1) => {
  const rows = Array.isArray(parcels) && parcels.length ? parcels : [];
  if (rows.length === 0) {
    return 'Please add at least one package';
  }

  for (let i = 0; i < rows.length; i++) {
    const p = recomputeParcel(rows[i]);
    const label = `Package #${startSrNo + i}`;
    const name = String(p.package_name || p.article_type || '').trim();

    if (!name) return `${label}: Package type is required`;
    if (!(toNum(p.actual_weight) > 0)) return `${label}: Actual weight is required`;
    if (!(toNum(p.length_cm) > 0)) return `${label}: Length (L) is required`;
    if (!(toNum(p.width_cm) > 0)) return `${label}: Width (W) is required`;
    if (!(toNum(p.height_cm) > 0)) return `${label}: Height (H) is required`;
    if (!(toNum(p.qty, 0) >= 1)) return `${label}: Qty is required`;
    if (!(toNum(p.chargeable_weight) > 0)) {
      return `${label}: Chargeable weight is required`;
    }
  }

  return null;
};

export const MAX_PACKAGE_CLONE_COUNT = 100;

/** Clone a bus article row for bulk multiply (new sr_no, same type/rate/weight). */
export const cloneBusArticleConfig = (source, srNo) => ({
  ...emptyBusArticle(srNo),
  package_name: source.package_name || source.article_type || 'PACKAGE',
  article_type: source.article_type || source.package_name || 'PACKAGE',
  article_amount: source.article_amount ?? '',
  actual_weight: source.actual_weight ?? '',
  chargeable_weight: source.chargeable_weight ?? source.actual_weight ?? '',
  qty: source.qty ?? 1,
});

export const sumChargeableWeight = (parcels) =>
  Math.round(
    (parcels || []).reduce((sum, p) => sum + toNum(recomputeParcel(p).chargeable_weight), 0),
  );

export const sumParcelQty = (parcels) =>
  (parcels || []).reduce((sum, p) => sum + Math.max(toNum(p.qty, 1), 0), 0) || 1;

export const calcFreightFromRate = (ratePerKg, totalChargeable) =>
  Math.round(toNum(ratePerKg) * toNum(totalChargeable) * 100) / 100;

/** Freight for one cargo package = rate/kg × chargeable weight. */
export const parcelFreightAmount = (parcel, ratePerKg) =>
  calcFreightFromRate(ratePerKg, recomputeParcel(parcel).chargeable_weight);

export const sumParcelFreightAmount = (parcels, ratePerKg) =>
  Math.round(
    (parcels || []).reduce((sum, parcel) => sum + parcelFreightAmount(parcel, ratePerKg), 0) * 100,
  ) / 100;

export const parcelsForApi = (parcels) =>
  (parcels || []).map((p, idx) => {
    const row = recomputeParcel(p);
    const articleType = (p.article_type || row.package_name || '').toString().trim().toUpperCase() || null;
    return {
      sr_no: idx + 1,
      package_name: row.package_name || articleType || `Box ${idx + 1}`,
      article_type: articleType,
      article_amount: toNum(p.article_amount),
      actual_weight: toNum(row.actual_weight),
      length_cm: row.length_cm === '' || row.length_cm == null ? null : toNum(row.length_cm),
      width_cm: row.width_cm === '' || row.width_cm == null ? null : toNum(row.width_cm),
      height_cm: row.height_cm === '' || row.height_cm == null ? null : toNum(row.height_cm),
      qty: Math.max(toNum(row.qty, 1), 1),
      cbm: row.cbm,
      volumetric_weight: Math.round(toNum(row.volumetric_weight)),
      chargeable_weight: Math.round(toNum(row.chargeable_weight) || toNum(row.actual_weight)),
    };
  });

export const parcelsFromApi = (apiParcels) => {
  if (!Array.isArray(apiParcels) || apiParcels.length === 0) {
    return [emptyParcel(1)];
  }
  return apiParcels.map((p, idx) =>
    recomputeParcel({
      sr_no: p.sr_no || idx + 1,
      package_name: p.package_name || p.article_type || `Box ${idx + 1}`,
      article_type: p.article_type || p.package_name || '',
      article_amount: p.article_amount ?? '',
      actual_weight: p.actual_weight ?? '',
      length_cm: p.length_cm ?? '',
      width_cm: p.width_cm ?? '',
      height_cm: p.height_cm ?? '',
      qty: p.qty || 1,
      chargeable_weight: p.chargeable_weight ?? '',
      chargeable_manual: true,
    }),
  );
};

export const busArticlesFromApi = (apiParcels, shipmentFallback = null) => {
  if (Array.isArray(apiParcels) && apiParcels.length > 0) {
    return apiParcels.map((p, idx) => ({
      ...emptyBusArticle(idx + 1),
      sr_no: p.sr_no || idx + 1,
      package_name: p.package_name || p.article_type || 'PACKAGE',
      article_type: (p.article_type || p.package_name || 'PACKAGE').toString().toUpperCase(),
      article_amount: p.article_amount ?? '',
      actual_weight: p.actual_weight ?? p.chargeable_weight ?? '',
      chargeable_weight: p.chargeable_weight ?? p.actual_weight ?? '',
      qty: p.qty || 1,
    }));
  }
  // Legacy single-article bus shipment → one row
  if (shipmentFallback) {
    return [
      {
        ...emptyBusArticle(1),
        article_type: (shipmentFallback.article_type || 'PACKAGE').toString().toUpperCase(),
        package_name: shipmentFallback.article_type || 'PACKAGE',
        article_amount: shipmentFallback.article_amount ?? '',
        actual_weight: shipmentFallback.weight ?? '',
        chargeable_weight: shipmentFallback.weight ?? '',
        qty: shipmentFallback.no_of_articles || 1,
      },
    ];
  }
  return [emptyBusArticle(1)];
};

export const sumBusArticleQty = (articles) =>
  (articles || []).reduce((sum, a) => sum + Math.max(toNum(a.qty, 1), 0), 0) || 1;

export const sumBusArticleWeight = (articles) =>
  (articles || []).reduce((sum, a) => sum + toNum(a.actual_weight), 0);

export const sumBusArticleFreight = (articles) =>
  Math.round(
    (articles || []).reduce(
      (sum, a) => sum + toNum(a.article_amount) * Math.max(toNum(a.qty, 1), 1),
      0,
    ) * 100,
  ) / 100;

/** 1-based article number missing charges, or null when every row is charged. */
export const firstBusArticleWithoutCharge = (articles, freightByWeight = false) => {
  const rows = articles?.length ? articles : [emptyBusArticle(1)];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] || {};
    if (freightByWeight) {
      if (toNum(row.actual_weight) <= 0 && toNum(row.chargeable_weight) <= 0) {
        return index + 1;
      }
      continue;
    }
    if (toNum(row.article_amount) <= 0) {
      return index + 1;
    }
  }
  return null;
};

/** Sync header form fields from bus article rows (list/MLS compat). */
export const syncFormFromBusArticles = (articles) => {
  const rows = articles?.length ? articles : [emptyBusArticle(1)];
  const first = rows[0];
  return {
    article_type: (first.article_type || 'PACKAGE').toString().toUpperCase(),
    no_of_articles: sumBusArticleQty(rows),
    article_amount: toNum(first.article_amount),
  };
};

export const calcBusFreight = (articles, ratePerKg, ratePerKgEnabled) => {
  if (ratePerKgEnabled) {
    return calcFreightFromRate(ratePerKg, sumBusArticleWeight(articles));
  }
  return sumBusArticleFreight(articles);
};

export const DEFAULT_SHIPMENT_CHARGE_FLAGS = {
  insurance: { enabled: false, amount: 0, mode: 'fixed' },
  docket: { enabled: false, amount: 0 },
  handling: { enabled: false, amount: 0 },
  door_pickup: { enabled: false, amount: 0 },
  door_delivery: { enabled: false, amount: 0 },
  loading: { enabled: false, amount: 0 },
  unloading: { enabled: false, amount: 0 },
  toll: { enabled: false, amount: 0 },
  fuel_surcharge: { enabled: false, amount: 0 },
  packing: { enabled: false, amount: 0 },
};

/** @deprecated alias — use DEFAULT_SHIPMENT_CHARGE_FLAGS */
export const DEFAULT_CARGO_CHARGE_FLAGS = DEFAULT_SHIPMENT_CHARGE_FLAGS;

export const resolveShipmentChargeDefaults = (defaults) => {
  const merged = { ...DEFAULT_SHIPMENT_CHARGE_FLAGS };
  if (!defaults || typeof defaults !== 'object') return merged;
  Object.keys(merged).forEach((key) => {
    merged[key] = { ...merged[key], ...(defaults[key] || {}) };
  });
  if (merged.insurance) {
    merged.insurance = {
      ...merged.insurance,
      mode: merged.insurance.mode === 'percent' ? 'percent' : 'fixed',
    };
  }
  return merged;
};

/** Insurance ₹ from settings: fixed amount, or % of freight (rounded to rupee). */
export const calcInsuranceFromSettings = (insuranceCfg, freight) => {
  if (!insuranceCfg?.enabled) return 0;
  const amount = Number(insuranceCfg.amount) || 0;
  if (insuranceCfg.mode === 'percent') {
    return Math.round((Number(freight) || 0) * amount / 100);
  }
  return amount;
};

/** @deprecated alias — use resolveShipmentChargeDefaults */
export const resolveCargoDefaults = resolveShipmentChargeDefaults;
